import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import logoImg from '../assest/logo.png';
import signImg from '../assest/sallary-slip.jpeg';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { X, Download, Printer } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SalarySlipModal({ isOpen, onClose, payroll }) {
  const slipRef = useRef();

  if (!isOpen || !payroll) return null;

  // Formatting helpers
  const formatCurrency = (amount) => {
    const numeric = typeof amount === 'string' ? parseFloat(amount.replace(/[^\d.]/g, '')) : parseFloat(amount);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(numeric || 0);
  };

  const numberToWords = (val) => {
    if (!val) return 'Zero Only';
    let num = typeof val === 'string' ? parseFloat(val.toString().replace(/[^\d.]/g, '')) : parseFloat(val);
    if (isNaN(num) || num === 0) return 'Zero Only';
    
    const a = ['', 'One ', 'Two ', 'Three ', 'Four ', 'Five ', 'Six ', 'Seven ', 'Eight ', 'Nine ', 'Ten ', 'Eleven ', 'Twelve ', 'Thirteen ', 'Fourteen ', 'Fifteen ', 'Sixteen ', 'Seventeen ', 'Eighteen ', 'Nineteen '];
    const b = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    const convert = (n) => {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + a[n % 10] : '');
      if (n < 1000) return a[Math.floor(n / 100)] + 'Hundred ' + (n % 100 !== 0 ? 'and ' + convert(n % 100) : '');
      if (n < 100000) return convert(Math.floor(n / 1000)) + 'Thousand ' + (n % 1000 !== 0 ? convert(n % 1000) : '');
      if (n < 10000000) return convert(Math.floor(n / 100000)) + 'Lakh ' + (n % 100000 !== 0 ? convert(n % 100000) : '');
      return '';
    };

    return convert(Math.round(num)) + 'Only';
  };

  const handleDownloadPDF = async () => {
    const element = slipRef.current;
    if (!element) {
      toast.error("Slip reference not found!");
      return;
    }

    try {
      const canvas = await html2canvas(element, { 
        scale: 2,
        useCORS: true,
        logging: true,
        backgroundColor: "#ffffff",
        allowTaint: true,
        width: 650, // Force canvas width to match element
        onclone: (clonedDoc) => {
           // Ensure images are fully loaded in the clone
           const images = clonedDoc.getElementsByTagName('img');
           for(let i=0; i<images.length; i++) {
             images[i].style.display = 'block';
           }
        }
      });

      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      pdf.save(`SalarySlip_${payroll.name || 'Employee'}.pdf`);
      toast.success('Salary slip downloaded successfully');
    } catch (error) {
      console.error("PDF Download failed:", error);
      toast.error("Download failed. Please try again or use the print function.");
    }
  };

  const workingSalary = parseFloat((payroll.monthEarned || 0).toString().replace(/[^\d.]/g, '')) || 0;
  const traveling = parseFloat((payroll.traveling_allowance || 0).toString().replace(/[^\d.]/g, '')) || 0;
  const bonus = parseFloat((payroll.bonus || 0).toString().replace(/[^\d.]/g, '')) || 0;
  const deductions = parseFloat((payroll.deductions || 0).toString().replace(/[^\d.]/g, '')) || 0;
  const dynamicNetPayable = workingSalary + traveling + bonus - deductions;

  return createPortal(
    <div className="fixed inset-0 z-[999] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] overflow-hidden flex flex-col animate-in zoom-in duration-300">
        
        {/* Modal Header */}
        <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-indigo-600 rounded-lg flex items-center justify-center text-white">
              <Printer size={20} />
            </div>
            <div>
              <h3 className="font-black text-slate-800 tracking-tight">Salary Slip Preview</h3>
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">{payroll.month}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={handleDownloadPDF}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
            >
              <Download size={16} /> Download PDF
            </button>
            <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full text-slate-500 transition-colors">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body - Scrollable Area */}
        <div className="flex-1 overflow-y-auto p-12 bg-slate-200/60 flex justify-center">
          
          {/* SAFE WRAPPER TO PREVENT CLIPPING */}
          <div style={{ padding: '0px', background: 'transparent' }}>
            <div 
              ref={slipRef}
              style={{ 
                width: '650px', 
                background: '#ffffff', 
                padding: '40px 45px',
                fontFamily: '"Inter", "Helvetica", sans-serif',
                position: 'relative',
                borderRadius: '0px',
                border: '1px solid #e2e8f0',
                boxSizing: 'border-box',
                overflow: 'hidden'
              }}
            >
            {/* Header: Logo & Company Name */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2.5px solid #4f46e5', paddingBottom: '12px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <img src={logoImg} alt="Logo" style={{ width: '55px', height: '55px', objectFit: 'contain' }} />
                <div>
                   <h1 style={{ margin: 0, fontSize: '17px', fontWeight: 900, color: '#1e293b', letterSpacing: '-0.02em', textTransform: 'uppercase' }}>
                     SECURE INSIGHT FOREVER PVT. LTD.
                   </h1>
                   <p style={{ margin: '1px 0 0', fontSize: '9px', color: '#64748b', fontWeight: 600 }}>
                     Mahesh Nagar, Patna (Bihar) – 800024
                   </p>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                 <h2 style={{ margin: 0, fontSize: '22px', fontWeight: 900, color: '#4f46e5', letterSpacing: '0.05em' }}>PAYSLIP</h2>
              </div>
            </div>

            {/* Employee Info Section */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', padding: '15px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '4px', marginBottom: '20px', position: 'relative' }}>
              
              {/* PAID Watermark Background Layer */}
              <div style={{ 
                position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                opacity: 0.1, pointerEvents: 'none', zIndex: 0, transform: 'rotate(-15deg)'
              }}>
                <div style={{ border: '4px solid #10b981', color: '#10b981', padding: '10px 30px', borderRadius: '4px', fontSize: '45px', fontWeight: 950, letterSpacing: '6px' }}>PAID</div>
                <div style={{ fontSize: '12px', fontWeight: 900, color: '#10b981', marginTop: '4px' }}>SALARY CREDITED</div>
              </div>

              {/* Left Column */}
              <div style={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div>
                   <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>Employee Name</div>
                   <div style={{ fontSize: '13px', fontWeight: 900, color: '#1e293b' }}>{payroll.name}</div>
                </div>
                <div>
                   <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>Designation</div>
                   <div style={{ fontSize: '12px', fontWeight: 800, color: '#475569' }}>{payroll.designation}</div>
                </div>
                <div>
                   <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>Mobile No.</div>
                   <div style={{ fontSize: '12px', fontWeight: 800, color: '#475569' }}>{payroll.user?.phone || 'N/A'}</div>
                </div>
              </div>

              {/* Right Column */}
              <div style={{ zIndex: 1, display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'flex-end', textAlign: 'right' }}>
                <div>
                   <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>Pay Month</div>
                   <div style={{ fontSize: '13px', fontWeight: 900, color: '#1e293b' }}>{new Date(payroll.selectedMonth || payroll.month).toLocaleString('default', { month: 'long', year: 'numeric' }).toUpperCase()}</div>
                </div>
                <div>
                   <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>Pay Date</div>
                   <div style={{ fontSize: '12px', fontWeight: 800, color: '#475569' }}>{new Date().toLocaleDateString('en-GB', { day:'2-digit', month:'short', year:'numeric' })}</div>
                </div>
                <div>
                   <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '1px' }}>Employee ID</div>
                   <div style={{ fontSize: '12px', fontWeight: 800, color: '#475569' }}>{payroll.empId}</div>
                </div>
              </div>
            </div>            {/* Earnings Table */}
            <div style={{ marginBottom: '15px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', background: '#e0f2fe', color: '#0369a1', fontWeight: 900, fontSize: '10px', borderRadius: '4px 4px 0 0', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                <span>Earnings</span>
                <span>Amount (₹)</span>
              </div>
              <div style={{ border: '1px solid #e2e8f0', borderTop: 'none', borderRadius: '0 0 4px 4px' }}>
                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>Working Salary</span>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#1e293b' }}>₹ {workingSalary.toFixed(2)}</span>
                 </div>
                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>Traveling Cost</span>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#1e293b' }}>₹ {traveling.toFixed(2)}</span>
                 </div>
                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', borderBottom: '1px solid #f1f5f9' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#1e293b' }}>Bonus</span>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#1e293b' }}>₹ {bonus.toFixed(2)}</span>
                 </div>
                 <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 20px', borderRadius: '0 0 4px 4px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Total Working Days</span>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: '#1e293b' }}>{payroll.working_days || payroll.workingDays || 0} Days</span>
                 </div>
              </div>
            </div>

            {/* Total Highlight - REPLACED FLEX WITH FIXED HEIGHT WRAPPER FOR STABILITY */}
            <div style={{ background: '#0f172a', color: '#fff', borderRadius: '4px', height: '50px', marginBottom: '8px', padding: '0 25px', position: 'relative' }}>
               <h3 style={{ margin: 0, fontSize: '12px', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', lineHeight: '50px', display: 'inline-block' }}>Net Payable Amount</h3>
               <div style={{ fontSize: '20px', fontWeight: 900, color: '#22c55e', lineHeight: '50px', position: 'absolute', right: '25px', top: 0 }}>
                 {formatCurrency(dynamicNetPayable)}
               </div>
            </div>

            <div style={{ textAlign: 'right', fontSize: '10px', fontWeight: 700, color: '#64748b', marginBottom: '30px' }}>
               In Words: {numberToWords(dynamicNetPayable)}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginTop: '30px' }}>
              <div style={{ fontSize: '9px', color: '#94a3b8', fontStyle: 'italic', fontWeight: 600 }}>
                 * Computer generated payslip
              </div>
              <div style={{ textAlign: 'center' }}>
                 <div style={{ position: 'relative', marginBottom: '-10px' }}>
                   <img src={signImg} alt="Seal" style={{ width: '130px', height: '55px', objectFit: 'contain', opacity: 0.9 }} />
                 </div>
                 <div style={{ width: '160px', borderTop: '2.5px solid #1e293b', paddingTop: '5px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 900, color: '#1e293b' }}>Authorized Signatory</div>
                    <div style={{ fontSize: '9px', fontWeight: 700, color: '#64748b', marginTop: '1px' }}>Accountant:- Yasmin Parveen</div>
                 </div>
              </div>
            </div>

            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
